import { Alert, Box, Button, Paper, Tab, Tabs, TextField, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "../../components/ui/PageHeader";
import VariablePalette, { useFieldFocus } from "../../components/ui/VariablePalette";
import { AppDispatch } from "../../redux/store";
import { getAllSmsSettings, updateSmsContent } from "../../service/SmsSettingService";
import { showSuccessToast } from "../../utils/Toast";
import { smsLength, withExamples } from "./smsSegments";

/**
 * SMS'erne til deltagerne (superadmin). Samme opbygning som mails: teksten bygges i midten, variablerne
 * trækkes ind fra venstre, og til højre ses beskeden, som deltageren får den på sin telefon.
 *
 * En SMS har ingen opsætning - den er ren tekst - så det, der kan trækkes ind, er variablerne. Til gengæld
 * koster længden penge, og derfor står det hele tiden, hvor mange SMS'er beskeden bliver til.
 */

/** De fire beskeder, i den rækkefølge deltageren møder dem */
const MESSAGES = [
  { key: "inviteLinkSmsContent", needsPrivacy: true },
  { key: "reminderSmsContent", needsPrivacy: true },
  { key: "breefResultSmsContent", needsPrivacy: false },
  { key: "breefResultSmsContentForWinner", needsPrivacy: false },
] as const;

type MessageKey = (typeof MESSAGES)[number]["key"];

/** Variablerne, hver med sin forklaring. PRIVACYLINK skal med i invitation og påmindelse (GDPR art. 13). */
const VARIABLE_KEYS = [
  "PRIVACYLINK", "FIRSTNAME", "LASTNAME", "PHONE", "LINK", "BREEFEXPIRYDATE", "BREEFSTARTDATE", "TENANTFIRSTNAME",
  "TENANTLASTNAME", "USERPLACEMENT", "BREEFSCORE", "USERSCORE", "BREEFNAME", "PRICE", "NUMBEROFPARTICIPANTS",
];

export default function SmsSetting() {
  const [t] = useTranslation("global");
  const isMobile = useMediaQuery("(max-width:600px)");
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { fieldProps, insert } = useFieldFocus();

  const [id, setId] = useState("");
  const [texts, setTexts] = useState<Record<MessageKey, string>>({
    inviteLinkSmsContent: "",
    reminderSmsContent: "",
    breefResultSmsContent: "",
    breefResultSmsContentForWinner: "",
  });
  const [saved, setSaved] = useState<Record<MessageKey, string> | null>(null);
  const [current, setCurrent] = useState<MessageKey>("inviteLinkSmsContent");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getAllSmsSettings(dispatch, isMobile, navigate)
      .then((response: any) => {
        const setting = response?.[0];
        if (!setting) return;
        const loaded = {
          inviteLinkSmsContent: setting.inviteLinkSmsContent || "",
          reminderSmsContent: setting.reminderSmsContent || "",
          breefResultSmsContent: setting.breefResultSmsContent || "",
          breefResultSmsContentForWinner: setting.breefResultSmsContentForWinner || "",
        };
        setTexts(loaded);
        setSaved(loaded);
        setId(String(setting._id || ""));
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const message = MESSAGES.find((item) => item.key === current)!;
  const text = texts[current];
  // Længden måles på beskeden med eksempelværdier sat ind - det er den, deltageren får
  const length = useMemo(() => smsLength(withExamples(text)), [text]);
  const missingPrivacy = message.needsPrivacy && !text.includes("|*PRIVACYLINK*|");
  const changed = saved ? saved[current] !== text : false;

  const variables = VARIABLE_KEYS.map((key) => ({
    key,
    description: t(`smsSetting.vars.${key}`),
    required: key === "PRIVACYLINK" && message.needsPrivacy,
  }));

  const save = async () => {
    if (!id || missingPrivacy) return;
    setBusy(true);
    // Servicen svarer med den gemte indstilling, ikke med et HTTP-svar. Går det galt, viser den selv fejlen.
    const response: any = await updateSmsContent({ [current]: text } as any, id, isMobile, navigate);
    setBusy(false);
    if (response) {
      setSaved({ ...(saved || texts), [current]: text });
      showSuccessToast(t("smsSetting.saved"), isMobile);
    }
  };

  return (
    <Box>
      <PageHeader title={t("smsSetting.title")} description={t("smsSetting.intro")} />

      <Tabs
        value={current}
        onChange={(_event, next) => setCurrent(next)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none" } }}
      >
        {MESSAGES.map((item) => (
          <Tab key={item.key} value={item.key} label={t(`smsSetting.messages.${item.key}`)} />
        ))}
      </Tabs>

      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* Variablerne, der kan trækkes ind */}
        <Box sx={{ flex: "0 0 230px", minWidth: 200 }}>
          <VariablePalette variables={variables} onInsert={insert} usedIn={text} title={t("smsSetting.variables")} />
        </Box>

        {/* Beskeden */}
        <Box sx={{ flex: "1 1 380px", minWidth: 320 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t(`smsSetting.help.${current}`)}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={6}
            value={text}
            onChange={(event) => setTexts({ ...texts, [current]: event.target.value })}
            inputProps={{ "aria-label": t(`smsSetting.messages.${current}`) }}
            {...fieldProps}
          />
          <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <Typography variant="body2" color={length.messages > 1 ? "warning.main" : "text.secondary"}>
              {t("smsSetting.length", { characters: length.characters, messages: length.messages })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("smsSetting.lengthHelp")}
            </Typography>
          </Box>
          {length.unicode && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t("smsSetting.unicode", { characters: length.offenders.join(" ") })}
            </Alert>
          )}
          {missingPrivacy && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {t("smsSetting.privacyRequired")}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={save} disabled={busy || !changed || missingPrivacy}>
              {t("smsSetting.save")}
            </Button>
          </Box>
        </Box>

        {/* Sådan ser den ud på telefonen */}
        <Box sx={{ flex: "0 1 330px", minWidth: 280 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("smsSetting.preview")}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 4, border: "8px solid #323232", bgcolor: "#f6f5f3", minHeight: 240 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, textAlign: "center" }}>
              Breefer
            </Typography>
            <Box
              data-testid="sms-preview"
              sx={{
                bgcolor: "#ffffff",
                borderRadius: "16px 16px 16px 4px",
                p: 1.5,
                fontSize: 14,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                boxShadow: "0 1px 2px rgba(0,0,0,.12)",
              }}
            >
              {withExamples(text) || t("smsSetting.empty")}
            </Box>
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {t("smsSetting.previewHelp")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
